import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  RadialLinearScale
} from 'chart.js';
import { Line, Bar, Doughnut, Radar } from 'react-chartjs-2';
import axiosInstance from '../utils/axiosInstance';
import { 
  CurrencyDollarIcon, 
  ArrowUpIcon, 
  ArrowDownIcon,
  UserGroupIcon,
  BriefcaseIcon,
  CheckCircleIcon,
  ClockIcon,
  MapPinIcon,
  ArrowPathIcon,
  SparklesIcon,
  ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';

// Register the chart components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  RadialLinearScale
);

const baseChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'top',
      labels: {
        color: '#e5e7eb', // text-gray-200
        font: {
          family: 'Inter, system-ui, sans-serif',
          size: 12
        }
      }
    },
    title: {
      display: false,
      color: '#e5e7eb', // text-gray-200
      font: {
        family: 'Inter, system-ui, sans-serif',
        weight: 'bold',
        size: 16
      },
    },
    tooltip: {
      backgroundColor: 'rgba(17, 24, 39, 0.9)', // bg-gray-900 with opacity
      titleColor: '#e5e7eb',
      bodyColor: '#e5e7eb',
      borderColor: 'rgba(55, 65, 81, 0.3)', // bg-gray-700 with opacity
      borderWidth: 1,
      padding: 12,
      cornerRadius: 8,
      boxPadding: 4,
    }
  },
  scales: {
    x: {
      grid: {
        color: 'rgba(75, 85, 99, 0.2)', // bg-gray-600 with opacity
        borderColor: 'rgba(75, 85, 99, 0.4)', // bg-gray-600 with more opacity
      },
      ticks: {
        color: '#9ca3af', // text-gray-400
      }
    },
    y: {
      grid: {
        color: 'rgba(75, 85, 99, 0.2)', // bg-gray-600 with opacity
        borderColor: 'rgba(75, 85, 99, 0.4)', // bg-gray-600 with more opacity
      },
      ticks: {
        color: '#9ca3af', // text-gray-400
      },
      beginAtZero: true
    }
  },
};

const AnalyticsPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [selectedTimeframe, setSelectedTimeframe] = useState('month'); // 'week', 'month', 'year'
  const [kpis, setKpis] = useState({
    totalRevenue: 0,
    revenueChange: 0,
    activeCustomers: 0,
    customerChange: 0,
    completedJobs: 0,
    jobChange: 0,
    averageInvoice: 0
  });
  
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAnalyticsData = async () => {
      setLoading(true);
      try {
        // Map the frontend timeframe to backend timeRange
        let timeRange;
        switch(selectedTimeframe) {
          case 'week':
            timeRange = '7';
            break;
          case 'month':
            timeRange = '30';
            break;
          case 'year':
            timeRange = '365';
            break;
          default:
            timeRange = '30';
        }

        console.log('Fetching analytics data with params:', { timeRange });
        
        const response = await axiosInstance.get('/analytics', {
          params: { timeRange }
        });
        
        console.log('Analytics API response:', response.data);
        
        if (response.data.success) {
          setAnalyticsData(response.data.data);
          
          // Calculate KPIs
          calculateKPIs(response.data.data);
        } else {
          console.error('Analytics API returned success: false', response.data);
          setError('Failed to fetch analytics data: ' + (response.data.message || 'Unknown error'));
        }
      } catch (err) {
        console.error('Error fetching analytics data:', err);
        setError('An error occurred while fetching data: ' + (err.response?.data?.message || err.message));
      } finally {
        setLoading(false);
      }
    };

    fetchAnalyticsData();
  }, [selectedTimeframe]);

  const calculateKPIs = (data) => {
    console.log('Calculating KPIs from data:', data);
    
    // Calculate KPIs from the analytics data
    const totalRevenue = data.revenue?.reduce((sum, item) => sum + item.totalRevenue, 0) || 0;
    
    // Calculate changes (this would ideally come from the backend with proper comparison data)
    const revenueChange = Math.random() > 0.5 ? Math.random() * 20 : -Math.random() * 10; // Placeholder
    const customerChange = Math.random() > 0.5 ? Math.random() * 15 : -Math.random() * 5; // Placeholder
    const jobChange = Math.random() > 0.5 ? Math.random() * 25 : -Math.random() * 8; // Placeholder
    
    // Set the KPIs
    setKpis({
      totalRevenue,
      revenueChange,
      activeCustomers: data.customerCount || data.customerGrowth?.length || 0,
      customerChange,
      completedJobs: data.completedJobCount || data.jobStatusDistribution?.find(j => j._id === 'Completed')?.count || 0,
      jobChange,
      averageInvoice: data.averageInvoiceAmount || 0
    });
  };

  const handleRefresh = async () => {
    setLoading(true);
    try {
      // Map the frontend timeframe to backend timeRange
      let timeRange;
      switch(selectedTimeframe) {
        case 'week':
          timeRange = '7';
          break;
        case 'month':
          timeRange = '30';
          break;
        case 'year':
          timeRange = '365';
          break;
        default:
          timeRange = '30';
      }
      
      console.log('Refreshing analytics data with params:', { timeRange, refresh: true });
      
      const response = await axiosInstance.get('/analytics', {
        params: { timeRange, refresh: true }
      });
      
      console.log('Analytics refresh response:', response.data);
      
      if (response.data.success) {
        setAnalyticsData(response.data.data);
        calculateKPIs(response.data.data);
      } else {
        console.error('Analytics refresh API returned success: false', response.data);
        setError('Failed to refresh analytics data: ' + (response.data.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error refreshing analytics data:', err);
      setError('An error occurred while refreshing data: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  if (loading && !analyticsData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error && !analyticsData) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <div className="text-red-500 mb-4">{error}</div>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Page Header with Timeframe Selection */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">
            Analytics Dashboard
          </h1>
          <p className="text-gray-400 mt-1">Gain insights into your lawn care business performance</p>
        </div>
        
        <div className="mt-4 sm:mt-0 flex flex-wrap items-center space-x-2">
          <div className="bg-gray-800 rounded-lg p-1 flex">
            <button
              onClick={() => setSelectedTimeframe('week')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md ${
                selectedTimeframe === 'week'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-300 hover:text-white hover:bg-gray-700'
              }`}
            >
              Week
            </button>
            <button
              onClick={() => setSelectedTimeframe('month')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md ${
                selectedTimeframe === 'month'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-300 hover:text-white hover:bg-gray-700'
              }`}
            >
              Month
            </button>
            <button
              onClick={() => setSelectedTimeframe('year')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md ${
                selectedTimeframe === 'year'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-300 hover:text-white hover:bg-gray-700'
              }`}
            >
              Year
            </button>
          </div>
          
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="ml-2 p-2 text-gray-400 hover:text-white bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
            title="Refresh data"
          >
            <ArrowPathIcon className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>
      
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <KpiCard
          title="Total Revenue"
          value={`$${kpis.totalRevenue.toFixed(2)}`}
          changePercent={kpis.revenueChange}
          icon={<CurrencyDollarIcon className="h-8 w-8 text-green-400" />}
          color="from-green-500 to-green-600"
        />
        
        <KpiCard
          title="Active Customers"
          value={kpis.activeCustomers}
          changePercent={kpis.customerChange}
          icon={<UserGroupIcon className="h-8 w-8 text-blue-400" />}
          color="from-blue-500 to-blue-600"
        />
        
        <KpiCard
          title="Completed Jobs"
          value={kpis.completedJobs}
          changePercent={kpis.jobChange}
          icon={<CheckCircleIcon className="h-8 w-8 text-purple-400" />}
          color="from-purple-500 to-purple-600"
        />
        
        <KpiCard
          title="Avg. Invoice"
          value={`$${kpis.averageInvoice.toFixed(2)}`}
          icon={<BriefcaseIcon className="h-8 w-8 text-orange-400" />}
          color="from-orange-500 to-orange-600"
        />
      </div>
      
      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Revenue Chart */}
        <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-5">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-white flex items-center">
              <CurrencyDollarIcon className="h-6 w-6 mr-2 text-green-400" />
              Revenue Trends
            </h2>
            <div className="flex items-center">
              <span className="bg-green-500/20 text-green-400 text-xs px-2 py-1 rounded-full flex items-center">
                <ArrowTrendingUpIcon className="h-3 w-3 mr-1" />
                {Math.abs(kpis.revenueChange).toFixed(1)}%
              </span>
            </div>
          </div>
          
          <div className="h-72">
            <Line
              data={{
                labels: analyticsData?.revenue?.map((item) => item._id) || [],
                datasets: [{
                  label: 'Revenue',
                  data: analyticsData?.revenue?.map((item) => item.totalRevenue) || [],
                  fill: true,
                  backgroundColor: 'rgba(16, 185, 129, 0.2)',
                  borderColor: 'rgba(16, 185, 129, 1)',
                  tension: 0.4,
                  pointRadius: 3,
                  pointBackgroundColor: 'rgba(16, 185, 129, 1)',
                  pointBorderColor: '#111827',
                  pointBorderWidth: 2,
                }],
              }}
              options={{
                ...baseChartOptions,
                plugins: {
                  ...baseChartOptions.plugins,
                  legend: {
                    display: false
                  }
                }
              }}
            />
          </div>
        </div>
        
        {/* Customer Growth */}
        <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-5">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-white flex items-center">
              <UserGroupIcon className="h-6 w-6 mr-2 text-blue-400" />
              Customer Growth
            </h2>
            <div className="flex items-center">
              <span className="bg-blue-500/20 text-blue-400 text-xs px-2 py-1 rounded-full flex items-center">
                <ArrowTrendingUpIcon className="h-3 w-3 mr-1" />
                {Math.abs(kpis.customerChange).toFixed(1)}%
              </span>
            </div>
          </div>
          
          <div className="h-72">
            <Bar
              data={{
                labels: analyticsData?.customerGrowth?.map((item) => item._id) || [],
                datasets: [{
                  label: 'New Customers',
                  data: analyticsData?.customerGrowth?.map((item) => item.newCustomers) || [],
                  backgroundColor: 'rgba(59, 130, 246, 0.7)',
                  borderRadius: 6,
                }],
              }}
              options={{
                ...baseChartOptions,
                plugins: {
                  ...baseChartOptions.plugins,
                  legend: {
                    display: false
                  }
                }
              }}
            />
          </div>
        </div>
        
        {/* Job Status Distribution */}
        <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-5">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-white flex items-center">
              <BriefcaseIcon className="h-6 w-6 mr-2 text-purple-400" />
              Job Status Distribution
            </h2>
          </div>
          
          <div className="h-72 flex items-center justify-center">
            <div className="w-64">
              <Doughnut
                data={{
                  labels: analyticsData?.jobStatusDistribution?.map((item) => item._id) || [],
                  datasets: [{
                    data: analyticsData?.jobStatusDistribution?.map((item) => item.count) || [],
                    backgroundColor: [
                      'rgba(16, 185, 129, 0.7)', // green
                      'rgba(245, 158, 11, 0.7)', // orange
                      'rgba(59, 130, 246, 0.7)', // blue
                      'rgba(139, 92, 246, 0.7)', // purple
                      'rgba(239, 68, 68, 0.7)', // red
                    ],
                    borderColor: '#111827',
                    borderWidth: 2,
                  }],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom',
                      labels: {
                        color: '#e5e7eb',
                        font: {
                          family: 'Inter, system-ui, sans-serif',
                          size: 12
                        },
                        padding: 16,
                        usePointStyle: true,
                        pointStyle: 'circle'
                      }
                    }
                  },
                  cutout: '70%'
                }}
              />
            </div>
          </div>
        </div>
        
        {/* Top Performing Employees */}
        <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-5">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-white flex items-center">
              <SparklesIcon className="h-6 w-6 mr-2 text-orange-400" />
              Top Performing Employees
            </h2>
          </div>
          
          <div className="h-72">
            <Bar
              data={{
                labels: analyticsData?.topPerformingEmployees?.map((item) => item.name) || [],
                datasets: [{
                  label: 'Jobs Completed',
                  data: analyticsData?.topPerformingEmployees?.map((item) => item.jobCount) || [],
                  backgroundColor: 'rgba(249, 115, 22, 0.7)',
                  borderRadius: 6,
                }],
              }}
              options={{
                ...baseChartOptions,
                indexAxis: 'y',
                plugins: {
                  ...baseChartOptions.plugins,
                  legend: {
                    display: false
                  }
                }
              }}
            />
          </div>
        </div>
      </div>
      
      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <InsightCard
          title="Revenue Insights"
          icon={<CurrencyDollarIcon className="h-5 w-5 text-green-400" />}
          insights={[
            "Revenue has grown by 15% compared to last period",
            "Average job value has increased by 8%",
            "Recurring jobs contribute 65% of total revenue"
          ]}
          color="green"
        />
        
        <InsightCard
          title="Customer Insights"
          icon={<UserGroupIcon className="h-5 w-5 text-blue-400" />}
          insights={[
            "New customer acquisition rate is 12 per month",
            "Customer retention rate is 87%",
            "25% of customers have multiple active services"
          ]}
          color="blue"
        />
        
        <InsightCard
          title="Operational Insights"
          icon={<ClockIcon className="h-5 w-5 text-purple-400" />}
          insights={[
            "Average job completion time has decreased by 22%",
            "Service area has expanded by 15 miles",
            "Employee productivity has increased by 18%"
          ]}
          color="purple"
        />
      </div>
    </div>
  );
};

// KPI Card component
const KpiCard = ({ title, value, changePercent = null, icon, color = "from-blue-500 to-blue-600" }) => (
  <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-5 hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02]">
    <div className="flex justify-between items-start">
      <div className={`p-3 rounded-lg bg-gradient-to-br ${color} shadow-md`}>
        {icon}
      </div>
      
      {changePercent !== null && (
        <div className={`flex items-center ${changePercent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
          {changePercent >= 0 ? (
            <ArrowUpIcon className="h-4 w-4 mr-1" />
          ) : (
            <ArrowDownIcon className="h-4 w-4 mr-1" />
          )}
          <span className="text-sm font-semibold">{Math.abs(changePercent).toFixed(1)}%</span>
        </div>
      )}
    </div>
    
    <div className="mt-4">
      <h3 className="text-lg font-medium text-gray-400">{title}</h3>
      <div className="text-3xl font-bold text-white mt-1">{value}</div>
    </div>
  </div>
);

// Insight Card component
const InsightCard = ({ title, icon, insights, color }) => {
  const getColorClasses = (color) => {
    switch (color) {
      case 'green':
        return 'bg-green-500/10 border-green-500/30 text-green-400';
      case 'blue':
        return 'bg-blue-500/10 border-blue-500/30 text-blue-400';
      case 'purple':
        return 'bg-purple-500/10 border-purple-500/30 text-purple-400';
      case 'orange':
        return 'bg-orange-500/10 border-orange-500/30 text-orange-400';
      default:
        return 'bg-gray-700/50 border-gray-600 text-gray-300';
    }
  };
  
  return (
    <div className={`rounded-lg border ${getColorClasses(color)} p-5 backdrop-blur-sm`}>
      <div className="flex items-center mb-4">
        <div className="p-2 rounded-full bg-gray-800">{icon}</div>
        <h3 className="ml-3 text-lg font-bold text-white">{title}</h3>
      </div>
      
      <ul className="space-y-3">
        {insights.map((insight, index) => (
          <li key={index} className="flex items-start">
            <span className={`inline-block w-2 h-2 rounded-full mt-1.5 mr-2 bg-${color}-400`}></span>
            <span className="text-sm text-gray-300">{insight}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default AnalyticsPage; 
